import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import { BrandHeader } from "./Login";
import { PasskeySignInButton } from "@/components/PasskeySignInButton";

export default function ManagerLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      let dest = location.state?.from?.pathname;
      if (!dest) {
        if (user.role === "EMPLOYEE") dest = "/clock";
        else if (user.role === "MANAGER") dest = "/admin/manager/dashboard";
        else dest = "/admin";
      }
      navigate(dest, { replace: true });
    }
  }, [user, navigate, location]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
      const dest = u.role === "MANAGER" ? "/admin/manager/dashboard" : "/admin";
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col">
      <div className="px-6 pt-8 sm:px-12 sm:pt-12">
        <BrandHeader />
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pb-10">
        <div className="w-full max-w-md">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C] font-semibold">
            Manager access
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A2744] tracking-tight mt-1">
            Welcome
          </h2>
          <p className="mt-2 text-gray-500">
            Sign in with your admin email and password.
          </p>

          <form onSubmit={onSubmit} className="mt-7 space-y-5" data-testid="manager-login-form">
            <div>
              <Label htmlFor="email" className="text-[#1A2744]">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="manager@coffeelovers.nz"
                  className="pl-10 h-11"
                  data-testid="login-email"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-[#1A2744]">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-11 h-11"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-[#1A2744] hover:bg-gray-100 transition-colors"
                  data-testid="login-password-toggle"
                  tabIndex={0}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 text-base bg-[#1A2744] hover:bg-[#263A63] text-white font-semibold"
              data-testid="login-submit"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>

            <PasskeySignInButton defaultLandingPath="/admin" />
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-xs text-gray-500 hover:text-[#1A2744] underline underline-offset-4"
              data-testid="link-staff-login"
            >
              ← Staff sign-in
            </Link>
          </div>
        </div>
      </div>
      <footer className="mt-10 text-center text-[11px] text-gray-500" data-testid="login-footer">
        Developed and designed by{" "}
        <span className="font-semibold text-[#1A2744]">Carattere Technologies Limited</span>
      </footer>
    </div>
  );
}
